import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  TextField,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../api";
import { toast } from "react-toastify";
import { CURRENCY_UNITS } from "../../utils/answerUnitUtils";

const PURPLE = "#7c3aed";

function StudentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAnswerUnit, setShowAnswerUnit] = useState(true);
  const [mcqNoticeMessage, setMcqNoticeMessage] = useState("");
  const [mcqNoticeDefault, setMcqNoticeDefault] = useState("");
  const [defaultUnits, setDefaultUnits] = useState([]);
  const [customUnits, setCustomUnits] = useState([]);
  const [defaultCompounds, setDefaultCompounds] = useState([]);
  const [customCompounds, setCustomCompounds] = useState([]);
  const [newUnit, setNewUnit] = useState("");
  const [unitError, setUnitError] = useState("");
  const [newCompound, setNewCompound] = useState(["", ""]);
  const [compoundError, setCompoundError] = useState("");
  const [allowPaperDownload, setAllowPaperDownload] = useState(false);
  const [allowScanUpload, setAllowScanUpload] = useState(false);
  const [testAnswer, setTestAnswer] = useState("");
  const [testQuestion, setTestQuestion] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("admin/analytics/student-settings")
      .then((res) => {
        const d = res.data?.data ?? {};
        setShowAnswerUnit(!!d.show_answer_unit);
        setAllowPaperDownload(d.allow_paper_download === true);
        setAllowScanUpload(d.allow_scan_upload === true);
        setMcqNoticeMessage(d.mcq_notice_message || "");
        setMcqNoticeDefault(d.mcq_notice_message_default || "");
        setDefaultUnits(Array.isArray(d.answer_units_default) ? d.answer_units_default : []);
        setCustomUnits(Array.isArray(d.answer_units_custom) ? d.answer_units_custom : []);
        setDefaultCompounds(Array.isArray(d.answer_unit_compounds_default) ? d.answer_unit_compounds_default : []);
        setCustomCompounds(Array.isArray(d.answer_unit_compounds_custom) ? d.answer_unit_compounds_custom : []);
      })
      .catch(() => toast.error("Failed to load student settings."))
      .finally(() => setLoading(false));
  }, []);

  const addUnit = () => {
    const value = newUnit.trim();
    if (!value) return;
    const known = [...defaultUnits, ...customUnits].map((u) => String(u).toLowerCase());
    if (known.includes(value.toLowerCase())) {
      setUnitError(`"${value}" is already in the list.`);
      return;
    }
    setCustomUnits((prev) => [...prev, value]);
    setNewUnit("");
    setUnitError("");
  };

  const addCompound = () => {
    const parts = newCompound.map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) {
      setCompoundError("A compound needs at least two units.");
      return;
    }
    const key = parts.join("|").toLowerCase();
    const known = [...defaultCompounds, ...customCompounds].map((c) => c.join("|").toLowerCase());
    if (known.includes(key)) {
      setCompoundError("That combination already exists.");
      return;
    }
    setCustomCompounds((prev) => [...prev, parts]);
    setNewCompound(["", ""]);
    setCompoundError("");
  };

  const runTest = async () => {
    if (!testAnswer.trim()) return;
    setTesting(true);
    try {
      const res = await api.post("admin/analytics/student-settings/test-answer-unit", {
        correct_answer_text: testAnswer,
        question_text: testQuestion.trim() || null,
      });
      setTestResult(res.data?.data ?? null);
    } catch {
      toast.error("Could not test that mark scheme.");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("admin/analytics/student-settings", {
        show_answer_unit: showAnswerUnit,
        mcq_notice_message: mcqNoticeMessage.trim() || null,
        answer_units_custom: customUnits,
        answer_unit_compounds_custom: customCompounds,
        allow_paper_download: allowPaperDownload,
        allow_scan_upload: allowScanUpload,
      });
      const d = res.data?.data ?? {};
      if (d.mcq_notice_message) setMcqNoticeMessage(d.mcq_notice_message);
      if (Array.isArray(d.answer_units_custom)) setCustomUnits(d.answer_units_custom);
      if (Array.isArray(d.answer_unit_compounds_custom)) setCustomCompounds(d.answer_unit_compounds_custom);
      toast.success("Student settings saved.");
    } catch {
      toast.error("Failed to save student settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Units
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the expected unit (e.g. cm, kg, £) is shown next to the student&apos;s
            answer input for short-answer questions. The student types only the numeric value;
            the unit is appended automatically on submission. Units apply to Maths questions only
            — English, Verbal and Non-Verbal Reasoning answers never show a unit.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showAnswerUnit}
                  onChange={(e) => setShowAnswerUnit(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show answer units to students
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showAnswerUnit ? "Units are currently visible." : "Units are currently hidden."}
                  </Typography>
                </Box>
              }
            />
          )}

          {!loading && showAnswerUnit && (
            <Box sx={{ mt: 3 }}>
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: "1px solid #eef0f4", borderRadius: 2, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Built-in units ({defaultUnits.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                    Recognised automatically. These cannot be removed — grading strips the same list,
                    so deleting one would stop answers wearing it from being marked correctly.
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {defaultUnits.map((u) => (
                      <Chip key={u} label={u} size="small" variant="outlined" />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Typography variant="body2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                Custom units
              </Typography>
              {customUnits.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  None yet — add a unit below if a paper uses one the list does not recognise.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                  {customUnits.map((u, i) => (
                    <Chip
                      key={`${u}-${i}`}
                      label={u}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={saving ? undefined : () => setCustomUnits((prev) => prev.filter((_, j) => j !== i))}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1.5 }}>
                <TextField
                  size="small"
                  label="Add a unit"
                  placeholder="e.g. furlongs"
                  value={newUnit}
                  onChange={(e) => { setNewUnit(e.target.value); setUnitError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUnit(); } }}
                  inputProps={{ maxLength: 32 }}
                  error={!!unitError}
                  helperText={unitError || "Written exactly as it appears at the end of the mark scheme."}
                  disabled={saving}
                  sx={{ maxWidth: 320 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addUnit}
                  disabled={saving || !newUnit.trim()}
                  sx={{ mt: 0.5, textTransform: "none" }}
                >
                  Add
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Compound units
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Answers that wear more than one unit (&quot;6 minutes 24 seconds&quot;, &quot;£22 60p&quot;,
                &quot;2 m 50 cm&quot;) get one numeric box per unit instead of a single box with one chip.
                A currency member (£, $, €, ¥) is shown <strong>before</strong> its box; every other unit
                after it. Each member also matches its abbreviations and singular form, so
                &quot;feet + inches&quot; covers &quot;5 ft 4 in&quot; too.
              </Typography>
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: "1px solid #eef0f4", borderRadius: 2, mb: 1.5, "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Built-in combinations ({defaultCompounds.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {defaultCompounds.map((c) => (
                      <Chip key={c.join("|")} label={c.join("  +  ")} size="small" variant="outlined" />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                {customCompounds.map((c, i) => (
                  <Box key={`${c.join("|")}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={c.join("  +  ")} size="small" color="primary" variant="outlined" />
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={() => setCustomCompounds((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                {newCompound.map((part, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {i > 0 && <Typography variant="body2" sx={{ fontWeight: 700 }}>+</Typography>}
                    <TextField
                      size="small"
                      placeholder={i === 0 ? "£ or metres" : "p or centimetres"}
                      value={part}
                      onChange={(e) => {
                        const next = [...newCompound];
                        next[i] = e.target.value;
                        setNewCompound(next);
                        setCompoundError("");
                      }}
                      inputProps={{ maxLength: 32 }}
                      disabled={saving}
                      sx={{ width: 130 }}
                    />
                  </Box>
                ))}
                {newCompound.length < 4 && (
                  <Button
                    size="small"
                    onClick={() => setNewCompound((prev) => [...prev, ""])}
                    disabled={saving}
                    sx={{ textTransform: "none" }}
                  >
                    + unit
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addCompound}
                  disabled={saving}
                  sx={{ textTransform: "none" }}
                >
                  Add compound
                </Button>
              </Box>
              {compoundError && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                  {compoundError}
                </Typography>
              )}
            </Box>
          )}

          {!loading && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Test a mark scheme
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                The chip only appears next to a student&apos;s answer box, so a unit this list does not
                recognise is invisible from the admin side. Paste a mark scheme exactly as it is stored
                to see the chip it produces.
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  label="Mark scheme (correct answer)"
                  placeholder="e.g. 9 3/4 hours"
                  value={testAnswer}
                  onChange={(e) => { setTestAnswer(e.target.value); setTestResult(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runTest(); } }}
                  inputProps={{ maxLength: 2000 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Question text (optional)"
                  placeholder="Used only when the mark scheme itself carries no unit"
                  value={testQuestion}
                  onChange={(e) => { setTestQuestion(e.target.value); setTestResult(null); }}
                  inputProps={{ maxLength: 5000 }}
                />
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={runTest}
                    disabled={testing || !testAnswer.trim()}
                    startIcon={testing ? <CircularProgress size={14} /> : null}
                    sx={{ textTransform: "none" }}
                  >
                    {testing ? "Testing…" : "Test"}
                  </Button>
                </Box>
              </Stack>

              {testResult && (
                <Paper
                  variant="outlined"
                  sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#fafafa" }}
                >
                  {testResult.compound_units ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Compound answer — one numeric box per unit:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {testResult.compound_units.map((u, i) => {
                          const isPrefix = CURRENCY_UNITS.includes(u);
                          const label = <Typography variant="body2" sx={{ fontWeight: 700 }}>{u}</Typography>;
                          return (
                            <Box key={`${u}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {isPrefix ? label : null}
                              <Box sx={{ px: 2, py: 0.75, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: "#fff", color: "#94a3b8" }}>
                                Your answer
                              </Box>
                              {isPrefix ? null : label}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  ) : testResult.unit ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        The student sees{testResult.source === "question wording" ? " (unit inferred from the question wording, not the mark scheme)" : ""}:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {testResult.prefix && (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{testResult.unit}</Typography>
                        )}
                        <Box sx={{ px: 2, py: 0.75, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: "#fff", color: "#94a3b8" }}>
                          Your answer
                        </Box>
                        {!testResult.prefix && (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{testResult.unit}</Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      No unit chip — the student gets a plain box and must type the unit themselves.
                      {testResult.unrecognised_suffix
                        ? ` The mark scheme ends in "${testResult.unrecognised_suffix}", which is not in the list above.`
                        : ""}
                    </Alert>
                  )}

                  {testResult.unrecognised_suffix && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{ mt: 1, textTransform: "none" }}
                      onClick={() => {
                        setNewUnit(testResult.unrecognised_suffix);
                        setUnitError("");
                      }}
                    >
                      Add &quot;{testResult.unrecognised_suffix}&quot; as a custom unit
                    </Button>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    Read as: {testResult.plain || "(empty)"}
                  </Typography>
                  {!testResult.enabled && (
                    <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                      Units are switched off above, so nothing is shown to students right now.
                    </Typography>
                  )}
                </Paper>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Written Papers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls the Download and Upload buttons on the student Past Papers and Mock Exams
            cards, for students who prefer to work on paper. Turning either off hides the button
            and blocks the endpoint.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowPaperDownload}
                    onChange={(e) => setAllowPaperDownload(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow students to download papers
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowPaperDownload
                        ? "Students can download a paper to print. Serves the original PDF where one is stored, otherwise a generated question paper. Answer keys are never included."
                        : "The download button is hidden and downloads are blocked."}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={allowScanUpload}
                    onChange={(e) => setAllowScanUpload(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Allow students to upload written papers for marking
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowScanUpload
                        ? "Students can photograph a paper they wrote by hand and have it marked. The result is recorded as a normal attempt."
                        : "The upload button is hidden and uploads are blocked."}
                    </Typography>
                  </Box>
                }
              />

              {allowScanUpload && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Each upload costs several AI vision calls plus marking. Drawing, matching and
                  ordering questions cannot be read from a scan — they are reported to the student
                  and score 0. Requires a worker on the <strong>scan-grading</strong> queue.
                </Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Convert-to-MCQ Notice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Message shown in the popup when a student first opens a paper or mock exam containing
            questions flagged as &quot;Recommend convert to MCQ&quot; (hard to answer online). The
            list of flagged question numbers is added below this message automatically. Leave empty
            to use the default message.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Popup message"
                value={mcqNoticeMessage}
                onChange={(e) => setMcqNoticeMessage(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                disabled={saving}
                placeholder={mcqNoticeDefault}
              />
              {mcqNoticeDefault && mcqNoticeMessage.trim() !== mcqNoticeDefault && (
                <Button
                  size="small"
                  sx={{ mt: 1, textTransform: "none" }}
                  onClick={() => setMcqNoticeMessage(mcqNoticeDefault)}
                  disabled={saving}
                >
                  Reset to default message
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function ParentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowAnswerKeyDownload, setAllowAnswerKeyDownload] = useState(true);

  useEffect(() => {
    api.get("admin/analytics/parent-settings")
      .then((res) => setAllowAnswerKeyDownload(!!res.data?.data?.allow_answer_key_download))
      .catch(() => toast.error("Failed to load parent settings."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("admin/analytics/parent-settings", {
        allow_answer_key_download: allowAnswerKeyDownload,
      });
      toast.success("Parent settings saved.");
    } catch {
      toast.error("Failed to save parent settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Key Downloads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, parents can download answer keys for past papers, mock exams, and worksheets
            (the &quot;With Answers&quot; worksheet download).
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={allowAnswerKeyDownload}
                  onChange={(e) => setAllowAnswerKeyDownload(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Allow parents to download answer keys
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {allowAnswerKeyDownload ? "Parents can download answer keys." : "Answer key downloads are hidden and blocked."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function PlaceholderTab({ label }) {
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {label} settings are not yet configured.
      </Alert>
    </Box>
  );
}

const TABS = ["Student", "Parent", "Tutor", "Tester", "System"];

const AdminSettings = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform-wide configuration settings.
        </Typography>
      </Box>

      <Paper
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid #eef0f4",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            px: { xs: 0.5, sm: 1 },
            bgcolor: "#fafafa",
            borderBottom: "1px solid #eef0f4",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              minHeight: 48,
              color: "#64748b",
            },
            "& .Mui-selected": { color: PURPLE },
            "& .MuiTabs-indicator": { backgroundColor: PURPLE, height: 3 },
          }}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && <StudentTab />}
          {tab === 1 && <ParentTab />}
          {tab === 2 && <PlaceholderTab label="Tutor" />}
          {tab === 3 && <PlaceholderTab label="Tester" />}
          {tab === 4 && <PlaceholderTab label="System" />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
